import { useCallback, useEffect, useState, type RefObject } from 'react';

/**
 * Full screen for one element. `active` follows the `fullscreenchange` event, not a local flag, so leaving full
 * screen the browser's way (Escape) keeps it right. `enabled` is false where the document may not go full screen.
 */
export function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const [enabled] = useState(() => document.fullscreenEnabled === true);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const onChange = () => setActive(ref.current !== null && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [ref]);
  // A refused request (no user gesture, a policy) leaves the page as it is.
  const toggle = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void ref.current?.requestFullscreen().catch(() => {});
  }, [ref]);
  return { enabled, active, toggle };
}
